import type { PageTranslations } from "@/modules/layouts/contexts/foLayout"

import { useMemo, useState } from "react"

import { PUBLIC_LOCALES } from "@/modules/locales/constants"
import { useLocale } from "@/modules/locales/useLocale"
import { getUpdatedHrefWithLocale } from "@/modules/routing/helpers/getUpdatedHrefWithLocale"
import { useHref } from "@/modules/routing/hooks/useHref"

type FoLayoutState = {
  hideFooter: () => void
  showFooter: () => void
  isFooterNeeded: () => boolean
  hideHeader: () => void
  showHeader: () => void
  isHeaderNeeded: () => boolean
  allowedLocales: PageTranslations
  host: string
}

/**
 * Hook to handle FO layout state for:
 * - Header / Footer visibility
 * - allowed locales in Header / Footer locales dropdown
 *
 * @return {FoLayoutState} - state value
 */
export const useFoLayoutState = (host = ""): FoLayoutState => {
  const href = useHref()
  const locale = useLocale()
  const isPageEmbedded = host.split(".").includes("embedded")

  const [footer, setFooter] = useState(true)
  const [header, setHeader] = useState(true)

  return useMemo(
    () => ({
      allowedLocales: PUBLIC_LOCALES.reduce(
        (previous, allowedLocale) => ({
          ...previous,
          [allowedLocale]: getUpdatedHrefWithLocale(href, locale, allowedLocale),
        }),
        {}
      ),
      hideFooter: () => {
        if (footer) {
          setFooter(false)
        }
      },
      hideHeader: () => {
        if (header) {
          setHeader(false)
        }
      },
      host,
      isFooterNeeded: () => !isPageEmbedded && !!footer,
      isHeaderNeeded: () => !isPageEmbedded && !!header,
      showFooter: () => {
        if (!footer) {
          setFooter(true)
        }
      },
      showHeader: () => {
        if (!header) {
          setHeader(true)
        }
      },
    }),
    [footer, header, host, isPageEmbedded, href, locale]
  )
}
